/**
 * CsvFormatHelp (issue #780)
 *
 * On-page documentation of the CSV structure accepted by the Bulk
 * Operations endpoint, plus a "Download CSV Template" action. Content
 * updates to match the currently-selected Entity Type so users don't have
 * to guess column names or required fields.
 */

import { CollapsibleSection } from '../../../components/shared';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import { UNIVERSAL_COLUMNS, buildCsvTemplate, getEntityCsvGuide } from './csvFieldGuide';
import './CsvFormatHelp.css';

const ForgeButton = 'forge-button' as React.ElementType;
const ForgeIcon = 'forge-icon' as React.ElementType;

export interface CsvFormatHelpProps {
  /** Currently-selected entity type, or '' when none is selected yet. */
  entityType: string;
}

/** Trigger a browser download of `content` as a file named `filename`. */
export function triggerCsvDownload(filename: string, content: string): void {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

export function CsvFormatHelp({ entityType }: CsvFormatHelpProps): JSX.Element {
  const guide = entityType ? getEntityCsvGuide(entityType) : null;
  const columns = guide ? [...UNIVERSAL_COLUMNS, ...guide.columns] : UNIVERSAL_COLUMNS;

  const handleDownload = (): void => {
    if (!entityType) return;
    triggerCsvDownload(`${entityType}-bulk-import-template.csv`, buildCsvTemplate(entityType));
  };

  return (
    <div className="csv-format-help">
      <CollapsibleSection
        headingId="csv-format-help-heading"
        title="CSV Format Help"
        testId="csv-format-help"
        defaultOpen={false}
      >
        <div className="csv-format-help__content">
          <ul className="csv-format-help__rules">
            <li>Encoding: UTF-8. Delimiter: comma. First row is the header row (column names).</li>
            <li>
              Quote a cell in double quotes if its value contains a comma, a double quote, or a line
              break; double any internal quotes (e.g.{' '}
              <code>&quot;He said &quot;&quot;hi&quot;&quot;&quot;</code> for the value{' '}
              <code>He said &quot;hi&quot;</code>).
            </li>
            <li>
              Column names may only contain letters, digits, spaces, <code>_</code>, <code>.</code>,
              and <code>-</code> — no brackets or other JSON-style punctuation.
            </li>
            <li>
              Only the <code>tags</code> column is split into an array (comma-separated). Every
              other array-valued field (e.g. <code>memberIds</code>, <code>stakeholderIds</code>) is
              written as a literal string via CSV — use the JSON items box below for those fields
              instead.
            </li>
            <li>
              Empty cells are always written as an empty string, never omitted. CSV upload in this
              page is only available for IMPORT and VALIDATE; UPDATE (MERGE/PATCH leave a field
              untouched when absent, REPLACE clears it) and DELETE use the JSON items box.
            </li>
            <li>
              Boolean columns must use the literal text <code>true</code> or <code>false</code>.
            </li>
            <li>
              Date columns use ISO 8601 (e.g. <code>2027-12-31</code>).
            </li>
          </ul>

          {entityType && guide ? (
            <>
              <p className="csv-format-help__summary">{guide.summary}</p>
              {columns.length > 0 && (
                <table className="csv-format-help__table">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Required</th>
                      <th>Format</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {columns.map((col) => (
                      <tr key={col.column}>
                        <td>{col.column}</td>
                        <td>{col.required ? 'Required' : 'Optional'}</td>
                        <td>{col.format}</td>
                        <td>{col.notes ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <div className="csv-format-help__actions">
                <ForgeButton type="button" variant="outlined" onClick={handleDownload}>
                  <ForgeIcon name="download" slot="start" aria-hidden="true" />
                  Download CSV Template
                </ForgeButton>
              </div>
            </>
          ) : (
            <p className="csv-format-help__summary">
              Select an entity type above to see its required and optional CSV columns and download
              a template.
            </p>
          )}
        </div>
      </CollapsibleSection>
    </div>
  );
}
